"use client";

import { type ChatAIUploadInput } from "@chat-ai/_utils/use-chat-ai-file-upload";
import { type ImageAIUploadInput } from "@image-ai/_utils/use-image-ai-file-upload";
import { uploadFile } from "@shared-utils/blob/block-upload-client";
import { safeAwait } from "@shared-utils/misc";
import { type ISearchUploadInput } from "@isearch/_utils/use-isearch-file-upload";
import { type FileType } from "@shared-database/schema";
import { type TranslateAIUploadInput } from "@translate-ai/_utils/use-translate-ai-file-upload";
import _ from "lodash";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { type TrainingAiUploadInput } from "@training-ai/_utils/use-training-ai-file-upload";
import { SUCCESSFUL_FILE_STATUSES, type FileStatusEnumType } from "@shared-database/enums";
import { type WorkerJobStatusEnumType } from "@shared-database/schema";
import type { SharedUploadInput } from "@shared-utils/use-shared-file-upload";
import { logFrontendError } from "@shared-utils/leo-logger";
import { PDF_FILE_MIME_TYPE } from "@shared-server/api/utils/file/constants";
import type { FileProcessingPhase } from "@shared/utils/rag/status/file-processing-state";

// Number of consecutive uploads
const MAX_UPLOAD = 3;
const PROCESSING_POLL_INITIAL_DELAY_MS = 3000;
const PROCESSING_POLL_MAX_DELAY_MS = 60_000;
const PROCESSING_POLL_BACKOFF_FACTOR = 2;
const ANALYZING_POLL_INTERVAL_MS = 5000;
const STATUS_CHECK_FAILED_POLL_MAX_ATTEMPTS = 10;
const RECOVERABLE_PROCESSING_FAILED_STATUS = "PROCESSING_FAILED";

// App and input type used for mapping with handlers
export type UploadType = {
  app: "CHAT_AI";
  input: ChatAIUploadInput;
} | {
  app: "IMAGE_AI";
  input: ImageAIUploadInput;
} | {
  app: "ISEARCH";
  input: ISearchUploadInput;
} | {
  app: "TRANSLATE_AI";
  input: TranslateAIUploadInput;
} | {
  app: "TRAINING_AI";
  input: TrainingAiUploadInput;
} | {
  app: "SHARED";
  input: SharedUploadInput;
};

// Metadata file type
type Metadata = {
  uploadUrl?: string;
  parentObjectId?: string;
} & FileType;

// Upload statuses
export type UploadStatus = "pending" | "creatingMetadata" | "metadataCreated" | "uploading" | "uploadCompleted" | Exclude<FileProcessingPhase, "failed"> | "removed" | "error";

// The upload item used internally and in the components using the functionality
export type UploadItem = {
  status: UploadStatus;
  metadata?: Metadata;
  error?: string;
  type: UploadType;
  file: File;
  data?: Record<string, unknown>;
  key: string;
  groupKey?: string;
  progress?: number | null;
  onStatusChange?: (item: UploadItem) => void | Promise<void>;
  onSuccess?: (item: UploadItem) => void | Promise<void>;
  onError?: (item: UploadItem) => void | Promise<void>;
};

// Handler for the upload
export type UploadHandler = {
  type: UploadType;
  createFileMetadata: (item: UploadItem) => Promise<Metadata | undefined>;
  updateFileMetadataStatus: (item: UploadItem, status: "UPLOADED" | "UPLOAD_FAILED") => Promise<FileType>;
  getFileStatus?: (item: UploadItem) => Promise<{
    id: string;
    status: FileStatusEnumType;
    errorText: string | null;
    processed: boolean;
    workerJobStatus?: WorkerJobStatusEnumType;
    graphReady?: boolean;
    progress?: number | null;
    phase?: FileProcessingPhase;
    graphError?: string | null;
  }>;
};

// Context
type FileUploaderContextType = {
  items: UploadItem[];
  addItem: (item: UploadItem) => void;
  removeItem: (uploadUrl: string) => void;
  addHandler: (handler: UploadHandler) => void;
};
export const FileUploaderContext = React.createContext<FileUploaderContextType | undefined>(undefined);

// Provider
interface FileUploadProviderProps {
  children: React.ReactNode;
}
export const FileUploadProvider = (props: FileUploadProviderProps) => {
  // Local state
  const [items, setItems] = useState<UploadItem[]>([]);
  const [handlers, setHandlers] = useState<UploadHandler[]>([]);
  const pollingTimeoutsRef = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map());

  // Cleanup polling timeouts on unmount
  useEffect(() => {
    const pollingTimeouts = pollingTimeoutsRef.current;
    return () => {
      pollingTimeouts.forEach(timeout => clearTimeout(timeout));
      pollingTimeouts.clear();
    };
  }, []);

  // Util: Get handler for item
  const getHandler = useCallback((item: UploadItem) => {
    // Find handler based on app and input
    const handler = handlers.find(h => h.type.app === item.type.app && h.type.input === item.type.input);
    if (!handler) throw new Error(`No handler found for upload type: ${JSON.stringify(item)}`);
    return handler;
  }, [handlers]);

  // Update item helper
  const updateItem = (itemUpdate: UploadItem) => {
    setItems(prev => prev.map(item => {
      // If not item with update or item is removed ignore all incuming updates
      if (item.status === "removed" || itemUpdate.key !== item.key) {
        return item;
      }
      const updatedItem = {
        ...item,
        ...itemUpdate
      };

      // Callbacks on status change
      const hasStatusUpdate = item.status !== itemUpdate.status;
      if (hasStatusUpdate) {
        void updatedItem.onStatusChange?.(updatedItem);
        if (updatedItem.status === "completed") {
          void updatedItem.onSuccess?.(updatedItem);
        } else if (updatedItem.status === "error") {
          void updatedItem.onError?.(updatedItem);
        }
      }
      return updatedItem;
    }));
  };

  // Create metadata - calls handler to create metadata/file instance on backend
  const createMetadata = async (item: UploadItem) => {
    updateItem({
      ...item,
      status: "creatingMetadata"
    });
    const [metadata, error] = await safeAwait(getHandler(item).createFileMetadata(item));
    if (error) {
      updateItem({
        ...item,
        status: "error",
        error: "Metadata creation failed"
      });
    } else {
      updateItem({
        ...item,
        metadata,
        status: "metadataCreated"
      });
    }
  };

  // Perform the upload of the file to the blob storage
  const upload = (item: UploadItem) => {
    updateItem({
      ...item,
      status: "uploading"
    });

    // Check that the item has metadata and upload URL.
    if (!item.metadata || !item.metadata.uploadUrl) {
      logFrontendError(new Error("Upload metadata or upload URL missing"), "Upload metadata or upload URL missing", "SHARED", {
        item
      });
      updateItem({
        ...item,
        status: "error"
      });
      return;
    }
    void uploadFile({
      file: item.file,
      meta: item.metadata as Metadata & {
        uploadUrl: string;
      },
      onSuccess: () => {
        updateItem({
          ...item,
          status: "uploadCompleted"
        });
      },
      onError: () => {
        updateItem({
          ...item,
          status: "error",
          error: "File upload failed"
        });
      },
      attachment: !isPreviewFileType(item.metadata)
    });
  };

  // Start polling for file processing status (worker completion)
  const startPolling = (item: UploadItem) => {
    const handler = getHandler(item);
    if (!handler.getFileStatus) {
      // No polling support — mark completed immediately
      updateItem({
        ...item,
        status: "completed"
      });
      return;
    }
    const metadata = item.metadata;
    if (!metadata) {
      updateItem({
        ...item,
        status: "error",
        error: "Upload metadata missing"
      });
      return;
    }
    const clearPolling = () => {
      const timeout = pollingTimeoutsRef.current.get(item.key);
      if (timeout) {
        clearTimeout(timeout);
      }
      pollingTimeoutsRef.current.delete(item.key);
    };
    clearPolling();
    let nextDelayMs = PROCESSING_POLL_INITIAL_DELAY_MS;
    let statusCheckFailedPollCount = 0;
    const scheduleNextPoll = () => {
      if (!pollingTimeoutsRef.current.has(item.key)) {
        return;
      }
      const timeout = setTimeout(() => {
        void poll();
      }, nextDelayMs);
      pollingTimeoutsRef.current.set(item.key, timeout);
      nextDelayMs = Math.min(nextDelayMs * PROCESSING_POLL_BACKOFF_FACTOR, PROCESSING_POLL_MAX_DELAY_MS);
    };
    const keepPolling = (params?: {
      error?: string;
      errorText?: string | null;
      progress?: number | null;
      phase?: FileProcessingPhase;
    }) => {
      const status: UploadStatus = params?.phase === "indexing" ? "indexing" : "processing";
      updateItem({
        ...item,
        metadata: {
          ...metadata,
          errorText: params?.errorText ?? metadata.errorText
        },
        status,
        error: params?.error,
        progress: params?.progress ?? item.progress
      });
      scheduleNextPoll();
    };
    const poll = async () => {
      try {
        const result = await handler.getFileStatus!(item);
        statusCheckFailedPollCount = 0;
        const fileStatus = result.status;
        if (fileStatus === RECOVERABLE_PROCESSING_FAILED_STATUS) {
          if (result.workerJobStatus === "QUEUED" || result.workerJobStatus === "STARTED") {
            keepPolling({
              error: result.errorText ? `${result.errorText}. Retrying...` : "File processing failed. Retrying...",
              errorText: result.errorText
            });
            return;
          }
        }
        if (!SUCCESSFUL_FILE_STATUSES.includes(fileStatus)) {
          clearPolling();
          updateItem({
            ...item,
            metadata: {
              ...metadata,
              status: fileStatus,
              errorText: result.errorText
            },
            status: "error",
            error: result.errorText || "File processing failed"
          });
          return;
        }
        if (result.processed) {
          const graphReady = result.graphReady ?? true;
          if (graphReady) {
            clearPolling();
            updateItem({
              ...item,
              metadata: {
                ...metadata,
                status: fileStatus,
                errorText: null
              },
              status: "completed",
              progress: 100,
              error: undefined
            });
          } else if (result.phase === "failed") {
            const errorText = result.graphError ?? result.errorText ?? "File analysis failed";
            clearPolling();
            updateItem({
              ...item,
              metadata: {
                ...metadata,
                status: fileStatus,
                errorText
              },
              status: "error",
              progress: 100,
              error: errorText
            });
          } else {
            updateItem({
              ...item,
              metadata: {
                ...metadata,
                status: fileStatus
              },
              status: "analyzing",
              progress: result.progress ?? item.progress
            });
            nextDelayMs = ANALYZING_POLL_INTERVAL_MS;
            scheduleNextPoll();
          }
          return;
        }
        keepPolling({
          errorText: null,
          progress: result.progress,
          phase: result.phase
        });
      } catch {
        statusCheckFailedPollCount += 1;
        if (statusCheckFailedPollCount > STATUS_CHECK_FAILED_POLL_MAX_ATTEMPTS) {
          clearPolling();
          updateItem({
            ...item,
            status: "error",
            error: "Failed to check file status"
          });
          return;
        }
        keepPolling({
          error: "Failed to check file status. Retrying..."
        });
      }
    };
    const initialTimeout = setTimeout(() => {
      void poll();
    }, nextDelayMs);
    pollingTimeoutsRef.current.set(item.key, initialTimeout);
    nextDelayMs = Math.min(nextDelayMs * PROCESSING_POLL_BACKOFF_FACTOR, PROCESSING_POLL_MAX_DELAY_MS);
  };

  // Update metadata - calls handler to update the file instance on backend
  const updateFileMetadata = async (item: UploadItem) => {
    updateItem({
      ...item,
      status: "processing"
    });
    const handler = getHandler(item);
    const [metadata, error] = await safeAwait(handler.updateFileMetadataStatus(item, "UPLOADED"));
    if (error) {
      updateItem({
        ...item,
        status: "error",
        error: "File processing failed"
      });
    } else if (!SUCCESSFUL_FILE_STATUSES.includes(metadata.status) && (metadata.status !== RECOVERABLE_PROCESSING_FAILED_STATUS || !handler.getFileStatus)) {
      updateItem({
        ...item,
        metadata,
        status: "error",
        error: "File processing failed"
      });
    } else if (handler.getFileStatus) {
      // Handler supports polling — worker was dispatched, wait for completion
      updateItem({
        ...item,
        metadata: {
          ...item.metadata,
          ...metadata
        },
        status: "processing"
      });
      startPolling({
        ...item,
        metadata: {
          ...item.metadata,
          ...metadata
        }
      });
    } else {
      updateItem({
        ...item,
        metadata: {
          ...item.metadata,
          ...metadata
        },
        status: "completed"
      });
    }
  };

  // Monitor items and process them
  useEffect(() => {
    // Start max 3 uploads in parallel
    const numberOfActiveUploads = items.filter(item => item.status !== "pending" && uploadItemInProgress(item.status));

    // Count pending items and start max 3 - activeUploads
    const pendingItems = items.filter(item => item.status === "pending");
    pendingItems.slice(0, Math.max(0, MAX_UPLOAD - numberOfActiveUploads.length)).forEach(item => {
      void createMetadata(item);
    });

    // Process items based on status
    items.forEach(item => {
      switch (item.status) {
        case "metadataCreated":
          void upload(item);
          break;
        case "uploadCompleted":
          void updateFileMetadata(item);
          break;
      }
    });
  },
  // eslint-disable-next-line react-hooks/exhaustive-deps
  [items]);
  return <FileUploaderContext.Provider value={{
    items,
    addItem: (item: UploadItem) => {
      setItems(prev => [...prev, item]);
    },
    addHandler: (handler: UploadHandler) => {
      // Only add if not already existing
      if (!handlers.find(h => _.isEqual(h.type, handler.type))) {
        setHandlers(prev => [...prev, handler]);
      }
    },
    removeItem: (key: string) => {
      const item = items.find(i => i.key === key);
      if (item) {
        const timeout = pollingTimeoutsRef.current.get(key);
        if (timeout) {
          clearTimeout(timeout);
          pollingTimeoutsRef.current.delete(key);
        }
        updateItem({
          ...item,
          status: "removed"
        });
      }
    }
  }}>
      {props.children}
    </FileUploaderContext.Provider>;
};
export const prettyStatusLabel = (status: UploadStatus) => {
  switch (status) {
    case "creatingMetadata":
    case "metadataCreated":
    case "uploading":
      return "Uploading";
    case "processing":
      return "Processing";
    case "indexing":
      return "Indexing";
    case "analyzing":
      return "Analyzing";
    default:
      return _.capitalize(status);
  }
};

// "analyzing" is intentionally NOT in this set: chunk search is live, so the
// composer (which gates on `uploadItemInProgress`) should unlock even though
// we still poll in the background for graph extraction to finish.
const progressStatuses = new Set<UploadStatus>(["pending", "creatingMetadata", "metadataCreated", "uploading", "uploadCompleted", "processing", "indexing"]);
export const uploadItemInProgress = (status: UploadStatus) => {
  return progressStatuses.has(status);
};

/**
 * True when the file still warrants a spinner in the UI, including the
 * post-processing "analyzing" phase where chat is unlocked but GraphRAG
 * extraction is still running in the background. Callers gating interactive
 * state (composer enable/disable) should use {@link uploadItemInProgress}
 * instead; this helper is only for visual "still working" indicators.
 */
export const uploadItemShowsLoader = (status: UploadStatus) => {
  return progressStatuses.has(status) || status === "analyzing";
};
const isPreviewFileType = (file: FileType | null) => {
  if (!file) {
    return false;
  }
  return file.mimeType === PDF_FILE_MIME_TYPE || `.${file.name.split(".").pop()}` || "";
};